import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip as ChartTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import {
  BookOpen, Clock, AlertTriangle, CheckCircle2, RefreshCw,
  TrendingUp, Building2, CreditCard,
  FileText, ArrowRight, Activity, Zap, ChevronRight,
  AlertCircle, Info, Bell, ReceiptText, Banknote,
  Users, DollarSign, Scale,
} from 'lucide-react';
import { useAuth }           from '@/hooks/useAuth';
import { usePermission }     from '@/hooks/usePermission';
import { dashboardApi }      from '@/api/dashboard.api';
import { AppRoutes }         from '@/constants/routes';
import { Permissions }       from '@/constants/permissions';
import { cn }                from '@/utils/cn';
import { formatCurrency, abbreviateNumber } from '@/utils/format';
import type {
  ExecutiveSummary,
  DashboardAlert,
  WorkflowSummary,
  ArApSummary,
  RecentActivity,
  RecentActivityItem,
} from '@/types/dashboardEnterprise.types';

// ── Format helpers ────────────────────────────────────────────────────────────

/** Compact currency for narrow KPI tiles: $1.2M, $692.8K, -$12.3K, $450.00 */
function fmtKpi(n: number): string {
  const abs  = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return (n < 0 ? '-' : '') + '$' + abs.toFixed(2);
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1)  return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Severity helpers ──────────────────────────────────────────────────────────

const SEVERITY_ICON: Record<string, React.ReactNode> = {
  critical: <AlertCircle size={15} className="flex-shrink-0" />,
  warning:  <AlertTriangle size={15} className="flex-shrink-0" />,
  info:     <Info size={15} className="flex-shrink-0" />,
};

// ── Module color map ──────────────────────────────────────────────────────────

const MODULE_COLORS: Record<string, string> = {
  GL:      'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  AR:      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  AP:      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  Banking: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  Recon:   'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
};

function moduleBadge(module: string) {
  const cls = MODULE_COLORS[module] ?? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
  return (
    <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold', cls)}>
      {module}
    </span>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function Skeleton({ w = 'w-24', h = 'h-4' }: { w?: string; h?: string }) {
  return <div className={cn('rounded animate-pulse bg-slate-200 dark:bg-slate-700', w, h)} />;
}

// ── KPI tile ──────────────────────────────────────────────────────────────────

interface KpiTileProps {
  label:      string;
  value:      string | number;
  subValue?:  string;
  subColor?:  'green' | 'red' | 'amber' | 'muted';
  icon:       React.ReactNode;
  iconColor:  string;
  loading?:   boolean;
  onClick?:   () => void;
}

function KpiTile({ label, value, subValue, subColor = 'muted', icon, iconColor, loading, onClick }: KpiTileProps) {
  const subCls = {
    green: 'text-emerald-600 dark:text-emerald-400 font-medium',
    red:   'text-rose-600 dark:text-rose-400 font-medium',
    amber: 'text-amber-600 dark:text-amber-400 font-medium',
    muted: 'text-slate-500 dark:text-slate-400',
  }[subColor];

  // Changed based on Dinisuru's request: High-contrast executive card styling
  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-xl p-3.5',
        'shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_0_rgba(0,0,0,0.06)]',
        'flex flex-col gap-2 transition-all duration-150',
        onClick && 'cursor-pointer hover:border-blue-400 dark:hover:border-blue-500'
      )}
    >
      <div className="flex items-center gap-2">
        <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-2xs', iconColor)}>
          {icon}
        </div>
        <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider leading-tight truncate">
          {label}
        </p>
      </div>
      <div className="min-w-0">
        {loading ? (
          <Skeleton w="w-20" h="h-7" />
        ) : (
          <p className="text-xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">{value}</p>
        )}
        {!loading && subValue && (
          <p className={cn('text-[11px] mt-1.5 leading-tight truncate', subCls)}>{subValue}</p>
        )}
        {loading && <Skeleton w="w-24" h="h-3" />}
      </div>
    </div>
  );
}

// ── Semi-circle Gauge (modern enterprise financial indicator) ─────────────────
// Changed based on Dinisuru's request: Replaced pastel gauge arcs and heavy colored borders
// with crisp, modern financial color grading and clean neutral tracks.

function SemiGauge({ pct }: { pct: number }) {
  const clamped     = Math.max(0, Math.min(100, Math.round(pct)));
  const fillColor   = clamped >= 70 ? '#10b981' : clamped >= 40 ? '#f59e0b' : '#ef4444';
  const trackColor  = '#e2e8f0';

  // Filled portion + empty remainder
  const gaugeData = [
    { value: clamped,       fill: fillColor   },
    { value: 100 - clamped, fill: 'transparent' },
  ];

  return (
    <div className="relative w-full flex flex-col items-center justify-center" style={{ height: 86 }}>
      <ResponsiveContainer width="100%" height={86}>
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          {/* Subtle background track */}
          <Pie
            data={[{ value: 100 }]}
            cx="50%"
            cy="85%"
            startAngle={180}
            endAngle={0}
            innerRadius={32}
            outerRadius={44}
            dataKey="value"
            stroke="none"
            fill={trackColor}
            isAnimationActive={false}
          />
          {/* Filled value arc */}
          <Pie
            data={gaugeData}
            cx="50%"
            cy="85%"
            startAngle={180}
            endAngle={0}
            innerRadius={32}
            outerRadius={44}
            paddingAngle={0}
            dataKey="value"
            stroke="none"
            cornerRadius={4}
          >
            {gaugeData.map((d, i) => <Cell key={i} fill={d.fill} />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {/* Centre value label */}
      <div className="absolute bottom-1 left-0 right-0 flex flex-col items-center">
        <span className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">
          {clamped}%
        </span>
      </div>
    </div>
  );
}

// ── Health Card ───────────────────────────────────────────────────────────────

function HealthCard({ title, pct, description, loading }: {
  title: string;
  pct: number;
  description: string;
  loading: boolean;
}) {
  const clamped     = Math.max(0, Math.min(100, Math.round(pct)));
  const statusLabel = clamped >= 70 ? 'Healthy' : clamped >= 40 ? 'Caution' : 'Critical';
  const statusCls   = clamped >= 70
    ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/80 dark:border-emerald-800/50'
    : clamped >= 40
    ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-800/50'
    : 'text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 border border-rose-200/80 dark:border-rose-800/50';

  const dotColor = clamped >= 70 ? 'bg-emerald-500' : clamped >= 40 ? 'bg-amber-500' : 'bg-rose-500';

  return (
    <div className={cn(
      'bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-xl p-4',
      'shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_0_rgba(0,0,0,0.06)]',
      'flex flex-col items-center text-center transition-all duration-150'
    )}>
      <div className="w-full flex items-center justify-between gap-2 mb-1">
        <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider truncate text-left">
          {title}
        </p>
        {!loading && (
          <span className={cn('inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0', statusCls)}>
            <span className={cn('w-1.5 h-1.5 rounded-full', dotColor)} />
            {statusLabel}
          </span>
        )}
      </div>

      <div className="w-full py-1">
        {loading ? (
          <div className="flex justify-center py-4"><Skeleton w="w-24" h="h-14" /></div>
        ) : (
          <SemiGauge pct={pct} />
        )}
      </div>

      <div className="w-full mt-1">
        {loading ? (
          <Skeleton w="w-32" h="h-3" />
        ) : (
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-tight line-clamp-2">{description}</p>
        )}
      </div>
    </div>
  );
}

// ── Aging Donut Tooltip ───────────────────────────────────────────────────────

const DonutTooltip = ({ active, payload }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { fill: string } }>;
}) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <span className="flex items-center gap-1.5 font-medium" style={{ color: p.payload.fill }}>
        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.payload.fill }} />
        {p.name}
      </span>
      <span className="block text-slate-900 dark:text-white font-semibold mt-0.5">{formatCurrency(p.value)}</span>
    </div>
  );
};

// ── Aging Bar Tooltip ─────────────────────────────────────────────────────────

const AgingTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 dark:text-slate-200 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5" style={{ color: p.color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-medium text-slate-900 dark:text-white">{formatCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── Activity Row ──────────────────────────────────────────────────────────────

function ActivityRow({ item, onClick }: { item: RecentActivityItem; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex items-start gap-3 py-2.5 px-4 border-b border-slate-100 dark:border-slate-800 last:border-0',
        onClick && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors'
      )}
    >
      <div className="flex-shrink-0 mt-0.5">{moduleBadge(item.module)}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-900 dark:text-white truncate">{item.documentNumber}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[200px]">{item.description}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-0.5">
            <Activity size={10} />
            {item.action}
          </span>
          <span>·</span>
          <span>{item.actor}</span>
        </div>
      </div>
      <span className="flex-shrink-0 text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap mt-0.5">
        {fmtTime(item.occurredAt)}
      </span>
    </div>
  );
}

// ── Aging slice colors ────────────────────────────────────────────────────────

const AGING_COLORS = ['#10b981', '#84cc16', '#f59e0b', '#f97316', '#ef4444'];
const AGING_LABELS = ['Current', '1–30d', '31–60d', '61–90d', '90d+'];

// ── Main Dashboard ────────────────────────────────────────────────────────────

export const DashboardPage = () => {
  const { user }              = useAuth();
  const navigate              = useNavigate();
  const { hasPermission }     = usePermission();

  const [exec,     setExec]     = useState<ExecutiveSummary | null>(null);
  const [alerts,   setAlerts]   = useState<DashboardAlert[]>([]);
  const [workflow, setWorkflow] = useState<WorkflowSummary | null>(null);
  const [arAp,     setArAp]     = useState<ArApSummary | null>(null);
  const [activity, setActivity] = useState<RecentActivity | null>(null);

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [asAt,    setAsAt]    = useState<Date>(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [execRes, alertsRes, wfRes, arApRes, actRes] = await Promise.all([
        dashboardApi.getExecutiveSummary(),
        dashboardApi.getAlerts(),
        dashboardApi.getWorkflowSummary(),
        dashboardApi.getArApSummary(),
        dashboardApi.getRecentActivity(25),
      ]);
      if (execRes.data?.data)    setExec(execRes.data.data);
      if (alertsRes.data?.data)  setAlerts(alertsRes.data.data ?? []);
      if (wfRes.data?.data)      setWorkflow(wfRes.data.data);
      if (arApRes.data?.data)    setArAp(arApRes.data.data);
      if (actRes.data?.data)     setActivity(actRes.data.data);
      setAsAt(new Date());
    } catch {
      setError('Failed to load dashboard data. Check connectivity and retry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived values ────────────────────────────────────────────────────────

  const netPosition = useMemo(() => {
    if (!exec) return null;
    return exec.arOutstandingBase - exec.apOutstandingBase;
  }, [exec]);

  const totalPending = useMemo(() => {
    if (!exec) return 0;
    return exec.journalsPendingApproval + exec.arInvoicesPendingApproval + exec.apBillsPendingApproval;
  }, [exec]);

  const arHealthPct = useMemo(() => {
    if (!arAp) return 0;
    const total = arAp.arCurrent + arAp.ar1To30 + arAp.ar31To60 + arAp.ar61To90 + arAp.arOver90;
    if (total === 0) return 100;
    return Math.round((arAp.arCurrent / total) * 100);
  }, [arAp]);

  const apHealthPct = useMemo(() => {
    if (!arAp) return 0;
    const total = arAp.apCurrent + arAp.ap1To30 + arAp.ap31To60 + arAp.ap61To90 + arAp.apOver90;
    if (total === 0) return 100;
    return Math.round((arAp.apCurrent / total) * 100);
  }, [arAp]);

  const approvalHealthPct = useMemo(() => {
    if (!exec) return 0;
    return Math.max(0, 100 - Math.min(100, totalPending * 10));
  }, [exec, totalPending]);

  const bankingHealthPct = useMemo(() => {
    if (!exec) return 0;
    if (exec.unreconciledBankCount === 0) return 100;
    return Math.max(20, 100 - exec.unreconciledBankCount * 25);
  }, [exec]);

  const arAgingDonutData = useMemo(() => {
    if (!arAp) return [];
    return [
      { name: 'Current', value: arAp.arCurrent,  fill: AGING_COLORS[0] },
      { name: '1–30d',   value: arAp.ar1To30,    fill: AGING_COLORS[1] },
      { name: '31–60d',  value: arAp.ar31To60,   fill: AGING_COLORS[2] },
      { name: '61–90d',  value: arAp.ar61To90,   fill: AGING_COLORS[3] },
      { name: '90d+',    value: arAp.arOver90,   fill: AGING_COLORS[4] },
    ].filter(d => d.value > 0);
  }, [arAp]);

  const apAgingDonutData = useMemo(() => {
    if (!arAp) return [];
    return [
      { name: 'Current', value: arAp.apCurrent,  fill: AGING_COLORS[0] },
      { name: '1–30d',   value: arAp.ap1To30,    fill: AGING_COLORS[1] },
      { name: '31–60d',  value: arAp.ap31To60,   fill: AGING_COLORS[2] },
      { name: '61–90d',  value: arAp.ap61To90,   fill: AGING_COLORS[3] },
      { name: '90d+',    value: arAp.apOver90,   fill: AGING_COLORS[4] },
    ].filter(d => d.value > 0);
  }, [arAp]);

  const agingBarData = useMemo(() => {
    if (!arAp) return [];
    const arValues = [arAp.arCurrent, arAp.ar1To30, arAp.ar31To60, arAp.ar61To90, arAp.arOver90];
    const apValues = [arAp.apCurrent, arAp.ap1To30, arAp.ap31To60, arAp.ap61To90, arAp.apOver90];
    return AGING_LABELS.map((label, i) => ({ label, ar: arValues[i], ap: apValues[i] }));
  }, [arAp]);

  const criticalAlerts = alerts.filter(a => a.severity === 'critical');

  // ── Quick Actions config ──────────────────────────────────────────────────

  const quickActions = [
    { label: 'New Journal',      icon: BookOpen,    route: AppRoutes.GL_JOURNAL_NEW,        permission: Permissions.GL_JOURNAL_CREATE,   color: 'violet' },
    { label: 'AR Invoice',       icon: ReceiptText, route: AppRoutes.AR_INVOICE_NEW,        permission: Permissions.AR_INVOICE_CREATE,   color: 'emerald' },
    { label: 'AP Bill',          icon: FileText,    route: AppRoutes.AP_BILL_NEW,           permission: Permissions.AP_BILL_CREATE,      color: 'orange' },
    { label: 'Bank Deposit',     icon: TrendingUp,  route: AppRoutes.BANK_DEPOSIT_NEW,      permission: Permissions.BNK_DEPOSIT_CREATE,  color: 'sky' },
    { label: 'Customer Receipt', icon: Users,       route: AppRoutes.AR_RECEIPT_NEW,        permission: Permissions.AR_RECEIPT_CREATE,   color: 'teal' },
    { label: 'AP Payment',       icon: Banknote,    route: AppRoutes.AP_PAYMENT_NEW,        permission: Permissions.AP_PAYMENT_CREATE,   color: 'rose' },
    { label: 'Bank Recon',       icon: Building2,   route: AppRoutes.BANK_RECONCILE_NEW,    permission: Permissions.BNK_RECONCILE_CREATE,'color': 'indigo' },
    { label: 'Card Recon',       icon: CreditCard,  route: AppRoutes.CARD_RECONCILIATIONS,  permission: Permissions.CARD_RECON_VIEW,     color: 'purple' },
  ] as const;

  const colorMap: Record<string, { icon: string; bg: string }> = {
    violet:  { icon: 'text-violet-600',  bg: 'bg-violet-50 dark:bg-violet-900/20'  },
    emerald: { icon: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
    orange:  { icon: 'text-orange-600',  bg: 'bg-orange-50 dark:bg-orange-900/20'  },
    sky:     { icon: 'text-sky-600',     bg: 'bg-sky-50 dark:bg-sky-900/20'        },
    teal:    { icon: 'text-teal-600',    bg: 'bg-teal-50 dark:bg-teal-900/20'      },
    rose:    { icon: 'text-rose-600',    bg: 'bg-rose-50 dark:bg-rose-900/20'      },
    indigo:  { icon: 'text-indigo-600',  bg: 'bg-indigo-50 dark:bg-indigo-900/20'  },
    purple:  { icon: 'text-purple-600',  bg: 'bg-purple-50 dark:bg-purple-900/20'  },
  };

  // ── Aging donut subcomponent ──────────────────────────────────────────────

  function AgingDonut({ title, data, rawArAp, side }: {
    title: string;
    data: { name: string; value: number; fill: string }[];
    rawArAp: ArApSummary | null;
    side: 'ar' | 'ap';
  }) {
    const buckets = rawArAp ? [
      { label: 'Current', value: side === 'ar' ? rawArAp.arCurrent : rawArAp.apCurrent, color: AGING_COLORS[0] },
      { label: '1–30d',   value: side === 'ar' ? rawArAp.ar1To30   : rawArAp.ap1To30,   color: AGING_COLORS[1] },
      { label: '31–60d',  value: side === 'ar' ? rawArAp.ar31To60  : rawArAp.ap31To60,  color: AGING_COLORS[2] },
      { label: '61–90d',  value: side === 'ar' ? rawArAp.ar61To90  : rawArAp.ap61To90,  color: AGING_COLORS[3] },
      { label: '90d+',    value: side === 'ar' ? rawArAp.arOver90  : rawArAp.apOver90,  color: AGING_COLORS[4] },
    ] : [];

    const allZero = buckets.every(b => b.value === 0);

    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-xl shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <DollarSign size={15} className="text-slate-500" />
          <span className="text-sm font-semibold text-slate-900 dark:text-white">{title}</span>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Skeleton w="w-32" h="h-32" />
            </div>
          ) : allZero ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400 gap-2.5">
              <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center text-emerald-500 border border-emerald-200/60 dark:border-emerald-800/40">
                <CheckCircle2 size={20} />
              </div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">No outstanding items</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={38}
                    outerRadius={55}
                    strokeWidth={2}
                  >
                    {data.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<DonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1">
                {buckets.map(b => (
                  <div key={b.label} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                      {b.label}
                    </span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {abbreviateNumber(b.value)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6 pb-8">

      {/* ── Section 1: Header ────────────────────────────────────────────── */}
      {/* Changed based on Dinisuru's request: Refined header with crisp typography and brand accent */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200/80 dark:border-slate-800 px-6 py-4.5 shadow-2xs">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5 tracking-tight">
              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center text-blue-600 border border-blue-200/60 dark:border-blue-800/40">
                <Zap size={18} className="text-blue-600" />
              </div>
              Finance Operations Control Center
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Welcome back, <span className="font-semibold text-slate-700 dark:text-slate-300">{user?.fullName ?? 'User'}</span> ·{' '}
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
              })}
              {!loading && (
                <span className="ml-1 text-slate-400 dark:text-slate-500">
                  · Updated {fmtTime(asAt.toISOString())}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {criticalAlerts.length > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs font-semibold text-red-700 dark:text-red-400">
                <Bell size={13} />
                {criticalAlerts.length} critical
              </div>
            )}
            <button
              onClick={load}
              disabled={loading}
              title="Refresh all data"
              className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 flex flex-col gap-6">

        {/* Error banner */}
        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
            <AlertCircle size={15} />
            {error}
          </div>
        )}

        {/* ── Section 2: Executive KPI Row ─────────────────────────────── */}
        <div>
          <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Key Performance Indicators
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">

            {/* 1. Net Position */}
            <KpiTile
              label="Net Position"
              value={netPosition !== null ? fmtKpi(netPosition) : '–'}
              subValue={exec
                ? `AR ${fmtKpi(exec.arOutstandingBase)} − AP ${fmtKpi(exec.apOutstandingBase)}`
                : undefined}
              subColor={netPosition !== null ? (netPosition >= 0 ? 'green' : 'red') : 'muted'}
              icon={<Scale size={16} className="text-teal-600" />}
              iconColor="bg-teal-100 dark:bg-teal-900/30"
              loading={loading}
            />

            {/* 2. AR Outstanding */}
            <KpiTile
              label="AR Outstanding"
              value={exec ? fmtKpi(exec.arOutstandingBase) : '–'}
              subValue={exec
                ? `${exec.arOverdueCount} overdue · ${fmtKpi(exec.arOverdueAmount)}`
                : undefined}
              subColor={exec && exec.arOverdueCount > 0 ? 'red' : 'muted'}
              icon={<ReceiptText size={16} className="text-emerald-600" />}
              iconColor="bg-emerald-100 dark:bg-emerald-900/30"
              loading={loading}
              onClick={() => navigate(AppRoutes.AR_INVOICES)}
            />

            {/* 3. AP Outstanding */}
            <KpiTile
              label="AP Outstanding"
              value={exec ? fmtKpi(exec.apOutstandingBase) : '–'}
              subValue={exec
                ? `${exec.apDueWithin7DaysCount} due in 7d · ${fmtKpi(exec.apDueWithin7DaysAmount)}`
                : undefined}
              subColor={exec && exec.apDueWithin7DaysCount > 0 ? 'amber' : 'muted'}
              icon={<Banknote size={16} className="text-orange-600" />}
              iconColor="bg-orange-100 dark:bg-orange-900/30"
              loading={loading}
              onClick={() => navigate(AppRoutes.AP_BILLS)}
            />

            {/* 4. Approvals Queue */}
            <KpiTile
              label="Approvals Queue"
              value={exec ? totalPending : '–'}
              subValue={exec
                ? `GL ${exec.journalsPendingApproval} · AR ${exec.arInvoicesPendingApproval} · AP ${exec.apBillsPendingApproval}`
                : undefined}
              subColor={totalPending > 0 ? 'amber' : 'green'}
              icon={<Clock size={16} className="text-amber-600" />}
              iconColor="bg-amber-100 dark:bg-amber-900/30"
              loading={loading}
              onClick={() => navigate(AppRoutes.GL_APPROVAL_INBOX)}
            />

            {/* 5. GL Journals */}
            <KpiTile
              label="GL Journals"
              value={exec?.journalsMtd ?? '–'}
              subValue={exec ? `${exec.journalsPostedToday} posted today` : undefined}
              subColor="muted"
              icon={<BookOpen size={16} className="text-violet-600" />}
              iconColor="bg-violet-100 dark:bg-violet-900/30"
              loading={loading}
              onClick={() => navigate(AppRoutes.GL_JOURNALS)}
            />

            {/* 6. Bank Recon */}
            <KpiTile
              label="Bank Recon"
              value={exec
                ? exec.unreconciledBankCount === 0 ? 'All Clear' : `${exec.unreconciledBankCount} pending`
                : '–'}
              subValue={exec
                ? exec.unreconciledBankCount === 0 ? 'All banks reconciled ✓' : 'Reconciliation pending'
                : undefined}
              subColor={exec
                ? exec.unreconciledBankCount === 0 ? 'green' : 'amber'
                : 'muted'}
              icon={<Building2 size={16} className="text-sky-600" />}
              iconColor="bg-sky-100 dark:bg-sky-900/30"
              loading={loading}
              onClick={() => navigate(AppRoutes.BANK_RECONCILE)}
            />
          </div>
        </div>

        {/* ── Section 3: Financial Health Score Row ─────────────────────── */}
        <div>
          <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Financial Health
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <HealthCard
              title="AR Collection Health"
              pct={arHealthPct}
              description="% of AR balance still current (not overdue)"
              loading={loading}
            />
            <HealthCard
              title="AP Obligation Health"
              pct={apHealthPct}
              description="% of AP balance within due date"
              loading={loading}
            />
            <HealthCard
              title="Approval Queue Health"
              pct={approvalHealthPct}
              description={`${totalPending} pending approvals · ${workflow?.totalOverdue ?? 0} overdue`}
              loading={loading}
            />
            <HealthCard
              title="Banking Health"
              pct={bankingHealthPct}
              description={`${exec?.unreconciledBankCount ?? '–'} bank account(s) pending reconciliation`}
              loading={loading}
            />
          </div>
        </div>

        {/* ── Section 4: AR/AP Aging Analysis ──────────────────────────── */}
        <div>
          <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            AR / AP Aging Analysis
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* AR Aging Donut */}
            <AgingDonut
              title="AR Aging Analysis"
              data={arAgingDonutData}
              rawArAp={arAp}
              side="ar"
            />

            {/* AP Aging Donut */}
            <AgingDonut
              title="AP Aging Analysis"
              data={apAgingDonutData}
              rawArAp={arAp}
              side="ap"
            />

            {/* Top Overdue Accounts */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-xl shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
                <AlertTriangle size={15} className="text-slate-500" />
                <span className="text-sm font-semibold text-slate-900 dark:text-white">Top Overdue Accounts</span>
              </div>
              {loading ? (
                <div className="divide-y divide-slate-100 dark:divide-slate-700">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 gap-4">
                      <div className="space-y-1.5 flex-1">
                        <Skeleton w="w-32" h="h-4" />
                        <Skeleton w="w-20" h="h-3" />
                      </div>
                      <Skeleton w="w-20" h="h-4" />
                    </div>
                  ))}
                </div>
              ) : !arAp ? (
                <div className="py-8 text-center text-slate-400 text-sm">No data.</div>
              ) : (
                <div className="overflow-y-auto max-h-72">
                  {arAp.topOverdueAr.length > 0 && (
                    <>
                      <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700/30">
                        <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                          AR — Overdue Customers
                        </span>
                      </div>
                      {arAp.topOverdueAr.map((item, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{item.name}</p>
                            <p className="text-xs text-red-500 dark:text-red-400">{item.daysOverdue}d overdue</p>
                          </div>
                          <span className="text-sm font-semibold text-slate-900 dark:text-white ml-4 whitespace-nowrap">
                            {formatCurrency(item.amount)}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                  {arAp.topOverdueAp.length > 0 && (
                    <>
                      <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700/30">
                        <span className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wide">
                          AP — Overdue Suppliers
                        </span>
                      </div>
                      {arAp.topOverdueAp.map((item, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{item.name}</p>
                            <p className="text-xs text-orange-500 dark:text-orange-400">{item.daysOverdue}d overdue</p>
                          </div>
                          <span className="text-sm font-semibold text-slate-900 dark:text-white ml-4 whitespace-nowrap">
                            {formatCurrency(item.amount)}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                  {arAp.topOverdueAr.length === 0 && arAp.topOverdueAp.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-400 gap-2.5">
                      <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center text-emerald-500 border border-emerald-200/60 dark:border-emerald-800/40">
                        <CheckCircle2 size={20} />
                      </div>
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">No overdue accounts</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Section 4b: AR/AP Aging Bar Chart ────────────────────────── */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-xl shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity size={15} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-900 dark:text-white">AR / AP Aging Comparison</span>
            </div>
            {arAp && !loading && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {arAp.arTotalInvoices} AR invoices · {arAp.apTotalBills} AP bills
              </p>
            )}
          </div>
          <div className="p-4">
            {loading ? (
              <div className="h-[200px] flex items-center justify-center">
                <Skeleton w="w-full" h="h-full" />
              </div>
            ) : agingBarData.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">
                No outstanding AR/AP data.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={agingBarData} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-100 dark:text-slate-800" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => abbreviateNumber(v)}
                  />
                  <ChartTooltip content={<AgingTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="ar" name="AR" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="ap" name="AP" fill="#f97316" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Section 5: Operational Intelligence ──────────────────────── */}
        <div>
          <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Operational Intelligence
          </h2>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

            {/* Alert Center */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Bell size={15} className="text-slate-500" />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">Alert Center</span>
                </div>
                {!loading && (
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="px-4 py-3 flex gap-3">
                      <Skeleton w="w-4" h="h-4" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton w="w-48" h="h-4" />
                        <Skeleton w="w-64" h="h-3" />
                      </div>
                    </div>
                  ))
                ) : alerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-400 gap-2">
                    <CheckCircle2 size={28} className="text-emerald-400" />
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">All clear — no alerts</p>
                    <p className="text-xs">Everything looks healthy.</p>
                  </div>
                ) : (
                  alerts.map((alert, i) => (
                    <div
                      key={i}
                      onClick={() => alert.actionRoute ? navigate(alert.actionRoute) : undefined}
                      className={cn(
                        'flex items-start gap-3 px-4 py-3 border-l-4 transition-colors',
                        alert.severity === 'critical' && 'border-l-red-500',
                        alert.severity === 'warning'  && 'border-l-amber-400',
                        alert.severity === 'info'     && 'border-l-blue-400',
                        alert.actionRoute && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30'
                      )}
                    >
                      <span className={cn('mt-0.5', {
                        'text-red-500':   alert.severity === 'critical',
                        'text-amber-500': alert.severity === 'warning',
                        'text-blue-500':  alert.severity === 'info',
                      })}>
                        {SEVERITY_ICON[alert.severity] ?? <Info size={15} className="flex-shrink-0" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{alert.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{alert.body}</p>
                      </div>
                      {alert.actionRoute && (
                        <ChevronRight size={14} className="flex-shrink-0 text-slate-400 mt-0.5" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Workflow Queue */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Activity size={15} className="text-slate-500" />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">Workflow Queue</span>
                </div>
                {workflow && !loading && (
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-slate-500 dark:text-slate-400">
                      {workflow.totalPending} pending
                    </span>
                    {workflow.totalOverdue > 0 && (
                      <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded font-semibold">
                        {workflow.totalOverdue} overdue
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="px-4 py-3 flex items-center justify-between gap-4">
                      <Skeleton w="w-28" h="h-4" />
                      <div className="flex gap-3">
                        <Skeleton w="w-12" h="h-5" />
                        <Skeleton w="w-12" h="h-5" />
                      </div>
                    </div>
                  ))
                ) : !workflow || workflow.modules.length === 0 ? (
                  <div className="py-8 text-center text-slate-400 text-sm">No workflow data.</div>
                ) : (
                  workflow.modules.map(mod => (
                    <div
                      key={mod.module}
                      onClick={() => navigate(mod.route)}
                      className="flex items-center px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{mod.module}</span>
                        {/* Progress bar */}
                        <div className="mt-1 h-1 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden w-full">
                          <div
                            className="h-full bg-amber-400 rounded-full transition-all"
                            style={{ width: `${(mod.pending / Math.max(1, workflow.totalPending)) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs flex-shrink-0">
                        {mod.pending > 0 ? (
                          <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full font-semibold">
                            {mod.pending} pending
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full font-semibold">
                            Clear
                          </span>
                        )}
                        {mod.overdue > 0 && (
                          <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full font-semibold">
                            {mod.overdue} overdue
                          </span>
                        )}
                      </div>
                      <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 6: Quick Actions + Activity Feed ──────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* Quick Actions */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
              <Zap size={15} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-900 dark:text-white">Quick Actions</span>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              {quickActions.map(qa => {
                const allowed = hasPermission(qa.permission);
                const colors  = colorMap[qa.color] ?? colorMap['violet'];
                const Icon    = qa.icon;
                return (
                  <button
                    key={qa.label}
                    onClick={() => allowed ? navigate(qa.route) : undefined}
                    disabled={!allowed}
                    title={allowed ? qa.label : 'Insufficient permissions'}
                    className={cn(
                      'flex flex-col items-center gap-1.5 p-3 rounded-lg border border-slate-100 dark:border-slate-700 text-center transition-colors',
                      allowed
                        ? 'hover:border-slate-300 dark:hover:border-slate-500 group cursor-pointer'
                        : 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center', colors.bg, colors.icon)}>
                      <Icon size={15} />
                    </span>
                    <span className={cn(
                      'text-xs font-medium leading-tight',
                      allowed
                        ? 'text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white'
                        : 'text-slate-500 dark:text-slate-500'
                    )}>
                      {qa.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recent Activity Feed */}
          <div className="xl:col-span-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Activity size={15} className="text-slate-500" />
                <span className="text-sm font-semibold text-slate-900 dark:text-white">Recent Activity</span>
                <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-semibold rounded-full">
                  Live
                </span>
              </div>
              <button
                onClick={() => navigate(AppRoutes.GL_JOURNALS)}
                className="flex items-center gap-1 text-xs text-teal-600 dark:text-teal-400 hover:underline font-medium"
              >
                View all <ArrowRight size={12} />
              </button>
            </div>
            <div className="overflow-y-auto max-h-80">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-2.5 border-b border-slate-100 dark:border-slate-800">
                    <Skeleton w="w-8" h="h-5" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton w="w-48" h="h-4" />
                      <Skeleton w="w-32" h="h-3" />
                    </div>
                    <Skeleton w="w-12" h="h-3" />
                  </div>
                ))
              ) : !activity || activity.items.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-sm">
                  No recent activity in the last 7 days.
                </div>
              ) : (
                activity.items.map((item, i) => (
                  <ActivityRow
                    key={i}
                    item={item}
                    onClick={item.route ? () => navigate(item.route!) : undefined}
                  />
                ))
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
